/*#
# Tags and filters

A tag is a bare key (`bug`) or `key:value` (`status:doing`). A **filter** is a
conjunction of terms over a task's tags — the one primitive every board,
lane, and query is built from. Resist adding to the grammar until a board
someone actually needs cannot be expressed.

| term | matches |
| --- | --- |
| `status` | any task with a `status` tag or a `status:*` tag |
| `status:doing` | exactly that tag |
| `-status` | negation: tasks with no `status` tag — the **inbox** |
| `project:none` | the implicit value: tasks with no `project` tag. Works for every key (`working:none` is nobody); `status:inbox` is its alias for status |
| `is:open` / `is:closed` / `is:any` | closed-ness; `is:open` is implied |
| `archived` / `archived:any` | only archived tasks / archived ones too; left out otherwise (archived means "don't look at it") |
| `type:practice` / `type:any` | what kind of entity (board #2472): practices, policies, guidance. With no `type:` term a filter finds tasks only (a task is the implicit type: `type:task` is no type tag); `type:any` finds every kind |
| `#38` | the task with that number, open or closed (a number names one task; an explicit `is:` still applies) |
| `~word` | substring of title or body (case-insensitive); a number also matches task numbers that contain it (`~92` finds #92 and #1392) |

```js
import { compileFilter } from 'tosijs-virta'

const inbox = compileFilter('-status')
const uiBugs = compileFilter('project:tosijs-ui bug is:any')

const task = { tags: ['bug', 'project:tosijs-ui'], closedAt: null, num: 7, title: 'x', body: '' }
preview.textContent = `inbox: ${inbox(task)}, uiBugs: ${uiBugs(task)}`
```
```test
test('the example evaluates both filters', () => {
  expect(preview.textContent).toBe('inbox: true, uiBugs: true')
})
```
*/

/*{ "parent": "Reference", "order": 20 }*/

import type { Task } from './model'
import { tagColor } from './tag-colors'

export interface TagParts {
  key: string
  value: string | null
}

/** Split `key:value` at the first colon; a bare key has `value: null`. */
export function parseTag(tag: string): TagParts {
  const i = tag.indexOf(':')
  if (i < 0) return { key: tag, value: null }
  return { key: tag.slice(0, i), value: tag.slice(i + 1) }
}

export function tagKey(tag: string): string {
  return parseTag(tag).key
}

/** Trim and collapse inner whitespace. Keys and values are case-sensitive. */
export function normalizeTag(tag: string): string {
  return tag.trim().replace(/\s+/g, ' ')
}

/**
 * The tags a string holds when it is several written as one —
 * "project:x priority:low kind:bug", from a space where a comma (or a
 * list) belonged — else null. Every word must be a `key:value` tag and
 * there must be more than one, so a value with a space stays one tag.
 */
export function joinedTags(tag: string): string[] | null {
  const words = tag.trim().split(/\s+/)
  if (words.length < 2) return null
  const tagLike = (w: string) => /^[a-z][a-z0-9-]*:(?!\/\/)\S+$/i.test(w)
  return words.every(tagLike) ? words : null
}

/**
 * The command-line flag a string swallowed — "status:backlog --quiet",
 * from a shell quote that took the flag after it into the value (board
 * #3465) — else null. A word that starts with `--` and a letter is never
 * part of a tag; one that only contains dashes ("a--b") is left alone.
 */
export function swallowedFlag(text: string): string | null {
  return text.split(/\s+/).find((w) => /^--[a-z][\w-]*(=.*)?$/i.test(w)) ?? null
}

/** All values a task carries for a key, in tag order. */
export function tagValues(tags: readonly string[], key: string): string[] {
  const out: string[] = []
  for (const tag of tags) {
    const parts = parseTag(tag)
    if (parts.key === key && parts.value !== null) out.push(parts.value)
  }
  return out
}

export function hasTagKey(tags: readonly string[], key: string): boolean {
  return tags.some((tag) => parseTag(tag).key === key)
}

export interface FilterTerm {
  negate: boolean
  key: string
  value: string | null
}

export interface Filter {
  terms: FilterTerm[]
  closed: 'open' | 'closed' | 'any'
  /** archived tasks: left out unless the filter asks (owner, 2026-09-26; board #753) */
  archived: 'hide' | 'any'
  /** entity types (board #2472): 'task' (the implicit type) unless the filter names a type or says any */
  types: 'task' | 'named' | 'any'
  nums: number[]
  text: string[]
}

/** Parse the filter grammar. Unknown shapes become tag terms; nothing throws. */
export function parseFilter(text: string): Filter {
  const filter: Filter = {
    terms: [],
    closed: 'open',
    archived: 'hide',
    types: 'task',
    nums: [],
    text: [],
  }
  let stated = false
  for (const raw of text.split(/\s+/)) {
    if (!raw) continue
    if (raw.startsWith('#')) {
      const n = Number(raw.slice(1))
      if (Number.isInteger(n)) filter.nums.push(n)
      continue
    }
    if (raw.startsWith('~')) {
      if (raw.length > 1) filter.text.push(raw.slice(1).toLowerCase())
      continue
    }
    const negate = raw.startsWith('-')
    const term = negate ? raw.slice(1) : raw
    if (!term) continue
    const { key, value } = parseTag(term)
    if (key === 'is' && !negate) {
      if (value === 'open' || value === 'closed' || value === 'any') {
        filter.closed = value
        stated = true
      }
      continue
    }
    if (key === ARCHIVED_TAG && !negate && value === 'any') {
      filter.archived = 'any'
      continue
    }
    // naming archived tasks asks for them: they are not left out by default
    if (key === ARCHIVED_TAG && !negate) filter.archived = 'any'
    // entity types (board #2472): a task is the implicit type
    if (key === TYPE_KEY) {
      if (value === 'any' && !negate) {
        filter.types = 'any'
        continue
      }
      if (value === TASK_TYPE) {
        // `type:task` is no type tag; `-type:task` is any other type
        filter.types = 'named'
        filter.terms.push({ negate: !negate, key, value: null })
        continue
      }
      filter.types = 'named'
    }
    filter.terms.push({ negate, key, value: value === '' ? null : value })
  }
  // `#38` names one task: it is found whether it is open or closed, unless
  // the filter says which it wants (board #46)
  if (filter.nums.length && !stated) filter.closed = 'any'
  // a number names one task: found whether it is archived or not, and
  // whatever its type
  if (filter.nums.length) filter.archived = 'any'
  if (filter.nums.length && filter.types === 'task') filter.types = 'any'
  return filter
}

/**
 * `<key>:none` is the implicit value of every key: a task with no such tag.
 * Nothing is ever written for it — the board's "no project" and "nobody"
 * lanes are this, and `status:inbox` is its named alias for status.
 */
export const NONE_VALUE = 'none'

export type Filterable = Pick<
  Task,
  'tags' | 'closedAt' | 'num' | 'title' | 'body'
>

function termMatches(tags: readonly string[], term: FilterTerm): boolean {
  let hit: boolean
  if (term.value === null) hit = hasTagKey(tags, term.key)
  else if (
    term.value === NONE_VALUE ||
    (term.key === 'status' && term.value === INBOX_STATUS)
  ) {
    // the implicit value: no tag with that key at all (or the value spelled out)
    hit =
      !hasTagKey(tags, term.key) || tags.includes(`${term.key}:${term.value}`)
  } else hit = tags.includes(`${term.key}:${term.value}`)
  return term.negate ? !hit : hit
}

export function matchesFilter(task: Filterable, filter: Filter): boolean {
  if (filter.archived === 'hide' && task.tags.includes(ARCHIVED_TAG))
    return false
  // tasks only, unless the filter names a type or says any (board #2472)
  if (filter.types === 'task' && hasTagKey(task.tags, TYPE_KEY)) return false
  if (filter.closed === 'open' && task.closedAt !== null) return false
  if (filter.closed === 'closed' && task.closedAt === null) return false
  if (filter.nums.length && !filter.nums.includes(task.num)) return false
  for (const term of filter.terms) {
    if (!termMatches(task.tags, term)) return false
  }
  if (filter.text.length) {
    const hay = `${task.title}\n${task.body}`.toLowerCase()
    // a number searched for is part of a task's number too (board #2438,
    // owner: `92` must keep #1392 in view): it finds #92, #1392, and any
    // text that says 92
    for (const needle of filter.text)
      if (
        !hay.includes(needle) &&
        !(/^\d+$/.test(needle) && String(task.num).includes(needle))
      )
        return false
  }
  return true
}

/** Parse once, test many. */
export function compileFilter(text: string): (task: Filterable) => boolean {
  const filter = parseFilter(text)
  return (task) => matchesFilter(task, filter)
}

/** Render a filter back to its text form (normalised). */
export function formatFilter(filter: Filter): string {
  const parts: string[] = []
  for (const t of filter.terms) {
    parts.push(
      `${t.negate ? '-' : ''}${t.key}${t.value === null ? '' : `:${t.value}`}`
    )
  }
  // only when it differs from what the filter implies: `#38` implies any
  const implied = filter.nums.length ? 'any' : 'open'
  if (filter.closed !== implied) parts.push(`is:${filter.closed}`)
  // archived:any unless something already implies it (a bare `archived`, a #n)
  if (
    filter.archived === 'any' &&
    !filter.nums.length &&
    !filter.terms.some((t) => t.key === ARCHIVED_TAG && !t.negate)
  )
    parts.push(`${ARCHIVED_TAG}:any`)
  if (filter.types === 'any' && !filter.nums.length)
    parts.push(`${TYPE_KEY}:any`)
  for (const n of filter.nums) parts.push(`#${n}`)
  for (const t of filter.text) parts.push(`~${t}`)
  return parts.join(' ')
}

/**
 * `priority:` values, most urgent first. A task with no priority sorts after
 * `low`. `blocked` is a bare tag: the task cannot move until something else
 * does (say what in a comment, or `blocked-by:#38`).
 */
/**
 * Size is a tag convention for teams that estimate: `size:<points>` on the
 * Fibonacci scale 1 2 3 5 8 13 21. Teams that stop at 8 just never write
 * 13; the last value reads as "n or more". Any other value sorts after
 * these; a lane shows its points when any card in it has a size.
 */
export const SIZE_KEY = 'size'
export const SIZE_ORDER = ['1', '2', '3', '5', '8', '13', '21']

/** The points a task carries, or null when unsized or not a number. */
export function sizeOf(tags: readonly string[]): number | null {
  const v = tagValues(tags, SIZE_KEY)[0]
  if (v === undefined) return null
  const n = Number(v.replace(/\+$/, ''))
  return Number.isFinite(n) ? n : null
}

/** Total points of the sized tasks, and how many were sized. */
export function sizePoints(tasks: readonly Task[]): {
  points: number
  sized: number
} {
  let points = 0
  let sized = 0
  for (const t of tasks) {
    const n = sizeOf(t.tags)
    if (n !== null) {
      points += n
      sized++
    }
  }
  return { points, sized }
}

/**
 * A continuation ("yes, and") is a new task that carries on from another:
 * `continues:#n` on the new one, `continued-by:#m` on the old one. It
 * inherits only the obvious tags — where it lives and what it is — never
 * state (status, size, working, blocked) or provenance.
 */
export const CONTINUES_KEY = 'continues'
export const CONTINUED_BY_KEY = 'continued-by'
export const INHERITED_KEYS = ['project', 'kind', 'area', 'priority']

export function inheritedTags(tags: readonly string[]): string[] {
  return tags.filter((t) => INHERITED_KEYS.includes(parseTag(t).key))
}

export const PRIORITY_ORDER = ['critical', 'high', 'medium', 'low']
export const BLOCKED_TAG = 'blocked'

/** Keys that name a relationship to another project: their chips say the key. */
export const RELATION_KEYS = ['blocks', 'delays', 'interested']

/**
 * Keys a task holds one value of (owner, 2026-09-25): adding `status:x`
 * replaces the status it had, and likewise its priority, its size, the
 * project that owns it and the person who owns it (`owner:`). The store
 * applies this to every tag write.
 */
export const EXCLUSIVE_KEYS = [
  'status',
  'priority',
  'size',
  'project',
  'owner',
  // the practices facet (board #2472): one of each
  'type',
  'rung',
  'mode',
  'visibility',
  // a task is part of one parent (board #755)
  'parent',
]

/**
 * What kind of entity an issue is (owner, 2026-09-30; board #2472): every
 * entity is an issue, and `type:` says which — `practice`, `policy`,
 * `guidance` (owner, 2026-09-30: "knowledge" was vague). No `type:` tag is a task, the implicit type, so every
 * existing task stays one and `kind:` keeps meaning the type of task.
 */
export const TYPE_KEY = 'type'
export const TASK_TYPE = 'task'
/** The types beside tasks, as the practices design names them. */
export const ENTITY_TYPES = ['practice', 'policy', 'guidance', 'skill'] as const
/** How far a practice has come (#2478): prose, then a checklist, then a skill. */
export const RUNGS = ['prose', 'checklist', 'skill'] as const
/** How a practice acts (#2474): only when asked, by suggesting, or on its own. */
export const MODES = ['manual', 'suggest', 'automatic'] as const
/** The person a task belongs to; handing it on keeps the last one subscribed. */
export const OWNER_KEY = 'owner'

/** 0 = critical … 3 = low, 4 = none. */
export function priorityRank(tags: readonly string[]): number {
  const value = tagValues(tags, 'priority')[0]
  const i = value === undefined ? -1 : PRIORITY_ORDER.indexOf(value)
  return i < 0 ? PRIORITY_ORDER.length : i
}

export function isBlocked(tags: readonly string[]): boolean {
  return tags.includes(BLOCKED_TAG) || hasTagKey(tags, 'blocked-by')
}

/**
 * How a tag is shown as a chip: the value alone (the key is the tooltip and
 * the color family), colored by the [tag color registry](/tag-colors/).
 */
export interface ChipStyle {
  text: string
  title: string
  bg: string
  ink: string
  /** `loud` chips (priority, bugs, blockers) are drawn first and bolder */
  loud: boolean
}

export function chipStyle(tag: string): ChipStyle {
  const { key, value } = parseTag(tag)
  const c = tagColor(tag)
  return {
    // a relationship reads as one: "blocks tosijs", not a bare "tosijs"
    text:
      value !== null && RELATION_KEYS.includes(key)
        ? `${key} ${value}`
        : // a part says what it is part of: "part of #12" (board #755)
        value !== null && key === 'parent'
        ? `part of ${value}`
        : value ?? key,
    title: tag,
    bg: c.bg,
    ink: c.ink,
    loud: !!c.loud,
  }
}

export type SortKey =
  | 'priority'
  | 'age-desc'
  | 'age-asc'
  | 'updated'
  | 'created'
  | 'num'
  | 'title'
  | 'size-desc'
  | 'size-asc'

export const SORT_LABELS: Record<SortKey, string> = {
  priority: 'priority',
  'age-desc': 'oldest in lane first',
  'age-asc': 'newest in lane first',
  updated: 'recently updated',
  created: 'recently created',
  num: 'number',
  title: 'title',
  'size-desc': 'biggest first',
  'size-asc': 'smallest first',
}

/**
 * Sort a copy. `priority` (default): most urgent first, blocked tasks
 * within a priority before unblocked, then oldest in lane first. `age-*`
 * is time since the last `status:` change. `updated`/`created` newest
 * first; `num` and `title` ascending; `size-*` by points, unsized last.
 */
export function sortTasks(
  tasks: readonly Task[],
  key: SortKey = 'priority'
): Task[] {
  const out = tasks.slice()
  const desc = (a: string, b: string) => (a < b ? 1 : a > b ? -1 : 0)
  const asc = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)
  switch (key) {
    case 'priority':
      out.sort(
        (a, b) =>
          priorityRank(a.tags) - priorityRank(b.tags) ||
          Number(isBlocked(b.tags)) - Number(isBlocked(a.tags)) ||
          asc(a.statusAt, b.statusAt) ||
          a.num - b.num
      )
      break
    case 'age-desc':
      out.sort((a, b) => asc(a.statusAt, b.statusAt) || a.num - b.num)
      break
    case 'age-asc':
      out.sort((a, b) => desc(a.statusAt, b.statusAt) || a.num - b.num)
      break
    case 'updated':
      out.sort((a, b) => desc(a.updatedAt, b.updatedAt) || a.num - b.num)
      break
    case 'created':
      out.sort((a, b) => desc(a.createdAt, b.createdAt) || a.num - b.num)
      break
    case 'num':
      out.sort((a, b) => a.num - b.num)
      break
    case 'title':
      out.sort((a, b) => a.title.localeCompare(b.title) || a.num - b.num)
      break
    case 'size-desc':
    case 'size-asc': {
      // unsized tasks last either way
      const size = (t: Task) => sizeOf(t.tags)
      out.sort((a, b) => {
        const x = size(a)
        const y = size(b)
        if (x === null || y === null)
          return Number(x === null) - Number(y === null) || a.num - b.num
        return (key === 'size-desc' ? y - x : x - y) || a.num - b.num
      })
      break
    }
  }
  return out
}

/**
 * Group tasks by the value of one tag key. Tasks without that key land in the
 * `''` lane — for `status`, that lane is the inbox.
 */
export function groupByTag(
  tasks: readonly Task[],
  key: string,
  missing = ''
): Map<string, Task[]> {
  const lanes = new Map<string, Task[]>()
  for (const task of tasks) {
    const values = tagValues(task.tags, key)
    const laneKeys = values.length ? values : [missing]
    for (const lane of laneKeys) {
      let bucket = lanes.get(lane)
      if (!bucket) lanes.set(lane, (bucket = []))
      bucket.push(task)
    }
  }
  return lanes
}

/**
 * "Working on" is a tag convention: `working:<identity>`. One identity works
 * on one thing at a time, so `VirtaStore.work()` moves the tag rather than
 * adding a second, unless told `also`.
 */
export const WORKING_KEY = 'working'

/**
 * A task belongs to one project (`project:<name>`), its owner. Handing it
 * to another project is `VirtaStore.move()`: the `project:` tag changes,
 * `from:<old>` keeps the provenance, and the old project stays a party to
 * it — by default `interested:<old>` (it still sees the task on its board),
 * or `blocks:<old>` / `delays:<old>` when it is waiting on it, which its
 * overview tile counts. `none` hands it over outright.
 */
export const PROJECT_KEY = 'project'
export const MOVE_RELATIONS = [
  'interested',
  'blocks',
  'delays',
  'none',
] as const
export type MoveRelation = (typeof MOVE_RELATIONS)[number]

/** Open tasks in other projects tagged as blocking or delaying `project`. */
export function waitingOn(tasks: readonly Task[], project: string): Task[] {
  return tasks.filter(
    (t) =>
      t.closedAt === null &&
      !tagValues(t.tags, PROJECT_KEY).includes(project) &&
      (tagValues(t.tags, 'blocks').includes(project) ||
        tagValues(t.tags, 'delays').includes(project))
  )
}

/** Open tasks another project owns that `project` is a party to: waiting on, or interested in. The project board shows them (board #139). */
export function concerning(tasks: readonly Task[], project: string): Task[] {
  return tasks.filter(
    (t) =>
      t.closedAt === null &&
      !tagValues(t.tags, PROJECT_KEY).includes(project) &&
      RELATION_KEYS.some((key) => tagValues(t.tags, key).includes(project))
  )
}

/** The identities currently working on a task, in tag order. */
export function workersOf(tags: readonly string[]): string[] {
  return tagValues(tags, WORKING_KEY)
}

/** Initials for an avatar: `agent:tosijs-ui` → `TU`, `owner` → `OW`. */
export function initialsOf(identity: string): string {
  const name = identity.includes(':')
    ? identity.slice(identity.indexOf(':') + 1)
    : identity
  const parts = name.split(/[^a-zA-Z0-9]+/).filter(Boolean)
  const letters =
    parts.length >= 2 ? parts[0]![0]! + parts[1]![0]! : name.slice(0, 2)
  return letters.toUpperCase()
}

/**
 * The canonical `status:` lanes, in board order. Everything is a todo, so
 * there is no `todo`: `inbox` is where things land, `backlog` is
 * acknowledged, `ready` is next up. Other values a board meets are shown
 * after these, alphabetically.
 *
 * `inbox` is the IMPLICIT status: a task with no `status:` tag is in it. No
 * tag is written; `status:inbox` in a filter matches such tasks, the board
 * lanes them there, and moving a task to the inbox removes its status.
 */
export const INBOX_STATUS = 'inbox'

/**
 * How a task can end (owner, 2026-09-26; board #753). Each is a status, and
 * a task in any of them is closed; leaving one reopens it. `done` is work
 * finished. The others are work that won't be done: `obsolete` (no longer
 * needed: replaced, duplicated, absorbed or overtaken) and `rejected`
 * (decided against, or never a task). Those two are archived when set, and
 * their lanes are hidden by default: nobody needs to see them again.
 */
export const FINISHED_STATUSES = ['done', 'obsolete', 'rejected'] as const
export const ARCHIVED_ON_ARRIVAL: readonly string[] = ['obsolete', 'rejected']
/**
 * "Don't look at it" (owner, 2026-09-26): a bare tag, orthogonal to status
 * and to how a task ended. Filters leave archived tasks out unless asked
 * (`archived`, `archived:any`); unarchiving restores exactly what was there.
 */
export const ARCHIVED_TAG = 'archived'
/**
 * The status a close ends in, from `close --as`: the finer words map onto
 * the endings, and `closed:<word>` keeps them.
 */
export const ENDING_FOR: Readonly<Record<string, string>> = {
  done: 'done',
  obsolete: 'obsolete',
  superseded: 'obsolete',
  duplicate: 'obsolete',
  absorbed: 'obsolete',
  // a practice that is no longer followed (board #2472): retired, never
  // deleted — it ends obsolete and keeps the word
  retired: 'obsolete',
  rejected: 'rejected',
  'not-a-task': 'rejected',
}
/** Statuses a task may still carry from an earlier model: they end it too. */
const RETIRED_ENDINGS = ['status:archive', 'status:superseded']
/** A task's tags say it has ended: a finished status, or a retired one. */
export const isFinished = (tags: readonly string[]): boolean =>
  tags.some(
    (t) =>
      RETIRED_ENDINGS.includes(t) ||
      (FINISHED_STATUSES as readonly string[]).includes(
        t.startsWith('status:') ? t.slice('status:'.length) : ''
      )
  )

export const STATUS_ORDER = [
  'inbox',
  'freezer',
  'backlog',
  'ready',
  'in-progress',
  'review',
  ...FINISHED_STATUSES,
]

/** Lanes hidden unless switched on: the deep freeze, and the endings that archive themselves. */
export const HIDDEN_BY_DEFAULT = ['freezer', ...ARCHIVED_ON_ARRIVAL]

/** Order lane values: inbox ('') first, canonical statuses in order, the rest alphabetically. */
export function orderLanes(values: Iterable<string>): string[] {
  const rank = (v: string) => {
    if (v === '') return -1
    const i = STATUS_ORDER.indexOf(v)
    return i < 0 ? STATUS_ORDER.length : i
  }
  return Array.from(values).sort(
    (a, b) => rank(a) - rank(b) || a.localeCompare(b)
  )
}
